<template>
  <div>
    <base-alert-dialog
      :show="archiveDialog.show"
      :variant="archiveDialog.variant"
      :title="archiveDialog.title"
      :message="archiveDialog.message"
      :show-cancel="archiveDialog.showCancel"
      :confirm-text="archiveDialog.confirmText"
      :confirm-variant="archiveDialog.confirmVariant"
      @confirm="onArchiveDialogConfirm"
      @cancel="archiveDialog.show = false"
    />
    <!--    Breadcrumbs-->
    <breadcrumb style="margin-left: -15px">
      <breadcrumb-item>
        <a href="/games/settings">Event settings</a>
      </breadcrumb-item>
      <breadcrumb-item v-if="$route.path === '/games/edit'">
        <span>{{ game_name }}</span>
      </breadcrumb-item>
      <breadcrumb-item v-if="$route.path === '/games/create'">
        <span>Create event</span>
      </breadcrumb-item>
    </breadcrumb>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>
    <div class="row" >
      <div class="col">
        <card class="no-scrollbar" style="overflow-x: scroll" card-body-classes="table-full-width">
          <div class="row mt-n2 mb-3">
            <div class="col">
              <tab-rail :tabs="categories" v-model="activeTabIndex">
                <template #actions>
                  <el-dropdown>
                    <base-button link type="primary" size="sm" title="More actions">
                      <i style="font-size: large" class="tim-icons el-icon-more"></i>
                    </base-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <a class="dropdown-item" v-if="!game.archived" @click.prevent="toggleActivationPrompt(game)" href="#">
                          <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px" class="inactive-dot mr-2"></span> Archive event
                        </a>
                        <a class="dropdown-item" v-if="game.archived" @click.prevent="toggleActivationPrompt(game)" href="#">
                          <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px" class="active-dot mr-2"></span> Activate event
                        </a>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </template>
              </tab-rail>
            </div>
          </div>

<!--          General-->
          <div v-if="activeTabIndex === 0">
            <collapsible-section title="Basics" icon="tim-icons icon-settings-gear-63">
              <!-- Three-column grid: identity fields on the first row, numbers on the second -->
              <div class="row event-metrics-row">
                <div class="col-12">
                  <div class="row">
                    <div class="col-md-4">
                      <base-input required label="Event name" :error="errs.name"
                        v-model="game.name"
                        placeholder="Event name">
                      </base-input>
                    </div>
                    <div class="col-md-4">
                      <base-input v-model="game.internal_name"
                                  label="Internal name"
                                  placeholder="Internal name">
                        <template #label>
                          <label>Internal name
                            <el-popover trigger="hover"
                                        placement="right">
                              <div>
                                <div class="popover-body">This value will be used on internal platform <br/> instead of event name.
                                  It will default to event<br/> name if left blank.
                                </div>
                              </div>
                              <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                            </el-popover>
                          </label>
                        </template>
                      </base-input>
                    </div>
                    <div class="col-md-4">
                      <base-input label="Event type">
                        <el-select v-model="game.vertical_id"
                                   class="select-primary"
                                   clearable
                                   placeholder="Select an event type"
                                   @change="onVerticalChange">
                          <el-option v-for="vertical in verticalOptions"
                                     class="select-primary"
                                     :label="vertical.name"
                                     :value="vertical.id"
                                     :key="'V' + vertical.id"/>
                        </el-select>
                      </base-input>
                    </div>
                    <div v-if="genreOptions.length > 0" class="col-md-4">
                      <base-input label="Genre">
                        <el-select v-model="game.genre_id"
                                   class="select-primary"
                                   clearable
                                   placeholder="Select a genre">
                          <el-option v-for="genre in genreOptions"
                                     class="select-primary"
                                     :label="genre.name"
                                     :value="genre.id"
                                     :key="'G' + genre.id"/>
                        </el-select>
                      </base-input>
                    </div>
                    <div class="col-md-4">
                      <base-input class="form-field--number" label="Event duration (minutes)" :error="errs.duration">
                        <el-input-number
                          v-model="game.duration_minutes"
                          :min="0"
                          :step="5"
                        />
                      </base-input>
                    </div>
                    <div class="col-md-4">
                      <div class="form-field--money form-group">
                        <label>Event build cost
                          <el-popover trigger="hover" placement="right">
                            <div>
                              <div class="popover-body">Internal only. Not shown to customers.</div>
                            </div>
                            <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                          </el-popover>
                        </label>
                        <div class="amount-input-group">
                          <span class="amount-prefix">{{ currency_symbol }}</span>
                          <base-input class="amount-input-wrapper mb-0">
                            <el-input-number :controls="false"
                                             v-model="game.build_cost"
                                             :min="0"
                                             :precision="2"
                                             placeholder="0.00" />
                          </base-input>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <base-input class="form-field--number" label="Square footage">
                        <template #label>
                          <label>Square footage
                            <el-popover trigger="hover" placement="right">
                            <div>
                              <div class="popover-body">Internal only. Not shown to customers.</div>
                            </div>
                            <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                          </el-popover>
                          </label>
                        </template>
                        <el-input-number
                          :controls="false"
                          v-model="game.square_footage"
                          :min="0"
                          :precision="0"
                          placeholder="0"
                        />
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>
            </collapsible-section>

            <collapsible-section title="Description" icon="tim-icons icon-paper">
              <div class="row">
                <div class="col-lg-6 event-rte-col">
                  <label>Event description</label>
                  <rich-text-editor
                    class="event-rte"
                    v-model="game.description"
                    height="auto"
                    toolbar="basic"
                    :allow-images="false"
                    placeholder="Enter event description..."
                  />
                </div>
                <div class="col-lg-6 event-rte-col">
                  <label>Booking page highlight
                    <el-popover trigger="hover"
                                placement="right"
                                width="260">
                      <div>
                        <div class="popover-body" style="word-break: normal; text-align: left;">A short highlight shown on the booking page "Learn more"
                          before a time is selected. It defaults to the full event description if left blank.
                          The full description is still used on the checkout description card.
                        </div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                  <rich-text-editor
                    class="event-rte"
                    v-model="game.short_description"
                    height="auto"
                    toolbar="basic"
                    :allow-images="false"
                    placeholder="Enter a short booking page highlight..."
                  />
                </div>
              </div>
            </collapsible-section>

            <collapsible-section title="Ratings" icon="tim-icons icon-shape-star" :badge="ratingsBadge">
              <event-ratings-editor v-model="ratings" :scales="ratingScales" :errors="errs.ratings" />
            </collapsible-section>

          </div>


<!--          Objectives-->
          <div v-if="activeTabIndex === 4">
            <objective-manager v-if="game.id" :game-id="game.id" :company-id="companyId" :company-group-id="companyGroupId" />
            <div v-else class="font-icon-detail mt-3">
              <span style="font-size: 3em"><i class="tim-icons icon-puzzle-10"></i></span>
              <h4 class="mt-3">Save the event first to manage objectives.</h4>
            </div>
          </div>

<!--          Images-->
          <div v-if="activeTabIndex === 1">
            <div class="row mb-2">
              <div class="col">
                <image-editor size="sm" text="Add image" :compose-modes="true" @saveImage="saveImage"></image-editor>
              </div>
            </div>
            <label style="font-size: small" class="card-title">
                  <span>Drag and drop to arrange images in the order that you prefer your customers to see them.
                    <el-popover trigger="hover"
                                placement="right">

                      <div>
                        <div class="popover-body">Recommended resolution sizes:
                          <br/> Landscape: <b>16:9</b> (1600x900 pixels)
                          <br/> Portrait (mobile): <b>3:4</b> (1080x1440 pixels)</div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </span>
            </label>

            <div class="row">
              <div class="col">
                <div class="mt-3 text-center" v-if="game_images.length <= 0">
                  <img src="/img/pandas/photo.png" class="empty-state-img" alt="No images" />
                  <h4 class="mt-2">No images have been uploaded yet</h4>
                </div>
              </div>
            </div>

            <div class="gift-cards-wrapper">
              <draggable v-model="game_images" :options="dragOptions" class="draggable-container">
                <div v-for="(card, i) in game_images" :key="i + 'L'" class="gift-card-item">
                  <div class="gift-card-container" :class="{ 'is-portrait': card.is_portrait }" :style="{'background-color': card.background_color}">
                    <div class="edit-button">
                      <image-editor :edited_image="card" :compose-modes="true" size="sm"
                                    :edit="true" @saveImage="updateImage"></image-editor>
                    </div>
                    <el-tooltip content="Delete image" effect="light" :open-delay="300" placement="top">
                      <base-button icon size="sm" type="danger" class="delete-button" @click="deleteImagePrompt(card)">
                        <i class="tim-icons icon-simple-remove"></i>
                      </base-button>
                    </el-tooltip>
                    <span v-if="!card.visible_to_customers" class="badge hidden-badge">Hidden</span>
                    <img :style="{'transform': 'translate(0%, 0%) scale(' + card.scale*0.01 + ')',
                  'left': card.x + 'px', 'top': card.y + 'px', 'object-fit':  card.object_fit, 'height': '100%'}"
                         style="cursor: all-scroll;" :src="card.url" alt="Event image">
                  </div>
                  <div class="mb-2">
                    <small style="font-style: italic" v-if="card.timestamp">Added on {{getLocalDate(card.timestamp)}}
                      , {{getLocalTime(card.timestamp)}}

                    </small>
                  </div>
                  <div class="row">
                    <div class="col">
                      <label class="d-block mb-1"> Show on
                        <el-popover trigger="hover" placement="right" width="260">
                          <div>
                            <div class="popover-body" style="text-align: left">Choose which devices customers see this image on. Desktop only images are hidden on phones, and mobile only images are shown only on phones. If no image matches a device, all images are shown.
                            </div>
                          </div>
                          <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                        </el-popover>
                      </label>
                      <segmented-toggle size="sm"
                                        v-model="card.device_target"
                                        :options="deviceTargetOptions" />
                    </div>
                  </div>
                  <div class="row mt-2">
                    <div class="col">
                      <el-switch active-color="#e14eca"
                                 v-model="card.visible_to_customers" />
                      <label> &nbsp; Visible to customers
                        <el-popover trigger="hover" placement="right">
                          <div>
                            <div class="popover-body"> Image will be displayed on the booking site, waiver page, and customer portal.
                            </div>
                          </div>
                          <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                        </el-popover>
                      </label>
                    </div>
                  </div>
                  <p class="mt-2">{{card.description}}</p>
                </div>
              </draggable>
            </div>

          </div>
<!--          Bookings and resources-->
          <div v-if="activeTabIndex === 2">

            <!-- BOOKING SITE SECTION -->
            <collapsible-section title="Booking site" icon="tim-icons icon-laptop">

              <div class="row">
                <div class="col-lg-3">
                  <label>Reservation style
                    <el-popover trigger="hover"
                                placement="right">
                      <div>
                        <div class="popover-body">A private booking reserves the entire event or time slot for a single group, <br/>
                          while a public booking allows multiple unrelated groups to book into the <br/>same event or slot.
                        </div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                  <base-input>
                    <el-select
                      v-model="game.is_public_booking"
                      class="select-primary"
                      value-key="id"
                      placeholder="Reservation style">
                      <el-option
                        v-for="(rs, index) in reservation_styles"
                        class="select-primary"
                        :label="rs.name"
                        :value="{id: rs.id,
                            name: rs.name}"
                        :key="'RS' + index"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>

              <div class="row mt-1 mb-2" v-if="game.is_public_booking?.id === 1">
                <div class="col">
                  <el-switch active-color="#e14eca"
                             v-model="game.allow_reservation_type_upgrade"  />
                  <label> &nbsp; Allow option to upgrade reservation type to private
                    <el-popover trigger="hover" placement="right">
                      <div>
                        <div class="popover-body">Enables customers to pay a flat fee to book out the entire public event slot.
                        </div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                </div>
              </div>

              <div class="row mt-1 mb-2" v-if="game.is_public_booking?.id === 1 && game.allow_reservation_type_upgrade">
                <div class="col-lg-4">
                  <label class="d-block">Upgrade pricing
                    <el-popover trigger="hover" placement="right" width="260">
                      <div>
                        <div class="popover-body" style="word-break: normal; text-align: left">Buyout charges one fixed amount for the whole slot. Tickets plus fee charges normal per ticket pricing plus a flat private booking fee.
                        </div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                  <segmented-toggle
                    variant="pills"
                    :value="game.reservation_upgrade_pricing_mode"
                    :options="reservation_upgrade_pricing_modes"
                    @input="onUpgradePricingModeChange" />
                </div>
              </div>

              <div class="row mt-1 mb-2" v-if="game.is_public_booking?.id === 1 && game.allow_reservation_type_upgrade">
                <div class="col">
                  <label>{{ upgradeFeeLabel }}</label>
                  <div style="max-width: 220px">
                    <money-input
                      v-model="game.reservation_upgrade_fee"
                      :currency-symbol="currency_symbol"
                      :min="0"
                      :precision="2"
                      placeholder="0.00" />
                  </div>
                </div>
              </div>

              <div class="row" >
                <div class="col d-flex align-items-center gap-2">
                  <div class="row" >
                    <div class="col">
                      <label>Minimum number of players</label>
                      <base-input style="width: 175px">
                        <el-input-number size="small" v-model="game.min_players_count" :min="1" :max="game.max_players_count"/>
                      </base-input>
                    </div>
                  </div>
                  <div class="row ml-1" >
                    <div class="col">
                      <label>Maximum number of players</label>
                      <base-input style="width: 175px">
                        <el-input-number size="small" v-model="game.max_players_count" :min="game.min_players_count" />
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

            </collapsible-section>

            <!-- RESOURCE MANAGEMENT SECTION -->
            <collapsible-section title="Resource management" icon="tim-icons icon-components">

              <div class="row mb-2">
                <div class="col">
                  <base-input :error="errs.resourceCategoryRequirements">
                    <el-switch active-color="#e14eca"
                               v-model="game.allocate_by_resource_category"  />
                    <label> &nbsp; This event requires multiple resource categories to run</label>
                  </base-input>
                </div>
              </div>

              <div class="row" v-if="game.allocate_by_resource_category">
                <div class="col-lg-6">
                  <div class="d-flex align-items-center p-2 border border-primary rounded mb-3">
                    <i class="text-white fas fa-info-circle mr-2"></i>
                    <small class="text-white">
                      Configure how many resources from each category this event requires to run.
                      The system will check availability across all specified categories before allowing bookings.
                    </small>
                  </div>
                </div>
              </div>

              <div class="row" v-if="game.allocate_by_resource_category">
                <div class="col-lg-6">
                  <div class="mb-2">
                    <base-button size="sm" type="primary" @click="addResourceCategoryRequirement">
                      <i class="tim-icons icon-simple-add mr-1"></i> Add category requirement
                    </base-button>
                  </div>

                  <!-- Category Requirements Table -->
                  <div v-if="resourceCategoryRequirements.length > 0" class="border-primary" >
                    <b-table :items="resourceCategoryRequirements"
                             :fields="[
                   { key: 'category', label: 'Resource category', tdClass: 'align-top' },
                   { key: 'allocation_type', label: 'Allocation type', tdClass: 'align-top' },
                   { key: 'required_count', label: 'Required count', tdClass: 'align-center' },
                   { key: 'actions', label: 'Actions', tdClass: 'align-top' }
                 ]">

                      <template v-slot:cell(category)="row">
                        <div style="min-width: 250px;">
                          <base-input :error="row.item.categoryError">
                            <el-select
                              v-model="row.item.category_id"
                              class="select-primary"
                              placeholder="Select category"
                              @change="onCategoryChange(row.index, $event)"
                            >
                              <el-option
                                v-for="category in availableResourceCategories"
                                :label="category.name"
                                :value="category.id"
                                class="select-primary"
                                :key="'CAT' + category.id"
                              >
                                <span :style="'color: ' + (category.color || '#666')">●</span>
                                {{ category.name }}
                              </el-option>
                            </el-select>
                          </base-input>
                        </div>
                      </template>

                      <template v-slot:cell(allocation_type)="row">
                        <div style="min-width: 130px;">
                          <base-input>
                            <el-select
                              v-model="row.item.allocation_type"
                              class="select-primary"
                              @change="onAllocationTypeChange(row)"
                            >
                              <el-option label="Fixed" value="fixed" class="select-primary" />
                              <el-option label="Tiered" value="tiered" class="select-primary" />
                            </el-select>
                          </base-input>
                        </div>
                      </template>

                      <template v-slot:cell(required_count)="row">
                        <div v-if="row.item.allocation_type === 'fixed'" style="min-width: 150px;">
                          <base-input :error="row.item.countError">
                            <el-input-number
                              size="small"
                              v-model="row.item.required_count"
                              :min="1"
                              :max="100"
                              placeholder="1"
                              :step="1"
                            />
                          </base-input>
                        </div>
                        <div v-else style="min-width: 150px;">
                          <base-input>
                            <a href="#" style="font-size: 13px; line-height: 40px; color: rgba(255,255,255,0.7);"
                               @click.prevent="$set(row.item, '_showDetails', !row.item._showDetails)">
                              {{ row.item.tiers.length }} {{ row.item.tiers.length === 1 ? 'tier' : 'tiers' }} configured
                              <i class="tim-icons" :class="row.item._showDetails ? 'icon-minimal-up' : 'icon-minimal-down'" style="font-size: 10px; margin-left: 4px;"></i>
                            </a>
                          </base-input>
                        </div>
                      </template>

                      <template v-slot:cell(actions)="row">
                        <el-tooltip content="Remove category requirement"
                                    effect="light"
                                    :open-delay="300"
                                    placement="top">
                          <base-button
                            @click="removeResourceCategoryRequirement(row.index)"
                            class="btn-link"
                            icon
                            size="sm"
                          >
                            <i class="tim-icons text-white icon-simple-remove"></i>
                          </base-button>
                        </el-tooltip>
                      </template>

                      <template v-slot:row-details="row">
                        <div class="p-2 mb-2 resource-tiers-panel">
                          <small class="text-white font-weight-bold d-block mb-2">Group size tiers</small>
                          <label v-if="row.item.tiersError" style="display: block; font-size: 12px; color: #ec250d;">
                            {{ row.item.tiersError }}
                          </label>
                          <label v-if="row.item.tiersWarning && !row.item.tiersError" style="display: block; font-size: 12px; color: #ff8d72;">
                            {{ row.item.tiersWarning }}
                          </label>
                          <div v-if="row.item.tiers.length > 0" class="resource-tiers-container">
                            <div v-for="(tier, tierIdx) in row.item.tiers"
                                 :key="'RTIER' + tierIdx"
                                 class="resource-tier-row"
                                 :class="{ 'tier-even': tierIdx % 2 === 1 }">
                              <!-- Tier number -->
                              <span class="resource-tier-index">{{ tierIdx + 1 }}</span>

                              <!-- Group size range (merged min — max) -->
                              <div class="resource-tier-field resource-tier-field--range">
                                <label v-if="tierIdx === 0" class="resource-tier-field-label">Group size</label>
                                <div class="resource-range-input-group">
                                  <el-input-number size="small" :controls="false"
                                    v-model="tier.min_group_size"
                                    :min="1" :step="1"
                                    @change="checkTierWarnings(row.index)" />
                                  <span class="resource-range-separator">&ndash;</span>
                                  <el-input-number size="small" :controls="false"
                                    v-model="tier.max_group_size"
                                    :min="tier.min_group_size || 1" :step="1"
                                    @change="checkTierWarnings(row.index)" />
                                </div>
                              </div>

                              <!-- Required count -->
                              <div class="resource-tier-field resource-tier-field--narrow">
                                <label v-if="tierIdx === 0" class="resource-tier-field-label">Required</label>
                                <div class="resource-single-input-group">
                                  <el-input-number size="small" :controls="false"
                                    v-model="tier.required_count"
                                    :min="1" :step="1" />
                                </div>
                              </div>

                              <!-- Blocking duration -->
                              <div class="resource-tier-field resource-tier-field--narrow">
                                <label v-if="tierIdx === 0" class="resource-tier-field-label">Duration (min)</label>
                                <div class="resource-single-input-group">
                                  <el-input-number size="small" :controls="false"
                                    v-model="tier.blocking_duration_minutes"
                                    :min="1" :step="5" />
                                </div>
                              </div>

                              <!-- Inline actions: remove + add -->
                              <div class="resource-tier-actions" :style="tierIdx === 0 ? 'margin-top: 18px' : ''">
                                <span class="resource-tier-action-slot">
                                  <button v-if="tierIdx > 0"
                                          class="resource-tier-remove-btn"
                                          @click="removeTier(row.index, tierIdx)">
                                    <i class="tim-icons icon-simple-remove"></i>
                                  </button>
                                </span>
                                <span class="resource-tier-action-slot">
                                  <button v-if="tierIdx === row.item.tiers.length - 1"
                                          class="resource-tier-add-btn"
                                          @click="addTier(row.index)">
                                    <i class="tim-icons icon-simple-add"></i>
                                  </button>
                                </span>
                              </div>
                            </div>
                          </div>
                          <div v-else class="text-muted text-center py-2 d-flex align-items-center justify-content-center" style="gap: 8px;">
                            <small>No tiers configured.</small>
                            <button class="resource-tier-add-btn" @click="addTier(row.index)">
                              <i class="tim-icons icon-simple-add mr-1"></i> Add tier
                            </button>
                          </div>
                        </div>
                      </template>
                    </b-table>
                  </div>
                </div>
              </div>
              <div class="row" v-if="!game.allocate_by_resource_category">
                <div class="col">
                  <label>Number of resources required to run a single event</label>
                  <div class="row">
                    <div class="col-lg-3">
                      <base-input style="width: 180px">
                        <el-input-number size="small" v-model="game.required_resource_count" :min="0" />
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-2">
                  <label>Pre-event buffer (minutes)
                    <el-popover trigger="hover"
                                placement="right">
                      <div>
                        <div class="popover-body">Time needed before an event starts where<br/>
                          resources should already be reserved. Use this<br/>
                          for setup or preparation before sessions.
                        </div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                  <base-input>
                    <el-input-number size="small" v-model="game.pre_buffer_minutes" :min="0" :max="120" :step="5" />
                  </base-input>
                </div>
                <div class="col-lg-2">
                  <label>Post-event buffer (minutes)
                    <el-popover trigger="hover"
                                placement="right">
                      <div>
                        <div class="popover-body">Time needed after an event ends before<br/>
                          resources become available again. Use this for<br/>
                          cleanup, reset, or preparation between sessions.
                        </div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                  <base-input>
                    <el-input-number size="small" v-model="game.buffer_minutes" :min="0" :max="120" :step="5" />
                  </base-input>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-3">
                  <label>Apply blocking rule to
                    <el-popover trigger="hover" placement="right">
                      <div>
                        <div class="popover-body">
                          Event slot: all groups booked into a slot share the resources assigned to it.<br/>
                          Purchase: each group that books takes its own resources, sized from its own group size.<br/>
                          Ticket: resource demand scales with the number of people booked.
                        </div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                  <base-input>
                    <el-select
                      v-model="game.blocking_unit"
                      class="select-primary"
                      placeholder="Status"
                      value-key="id"
                      v-on:change="j++"
                    >
                      <el-option
                        v-for="(bu, i) in block_units"
                        class="select-primary"
                        :label="bu.name"
                        :value="{id: bu.id, name: bu.name, label: bu.label}"
                        :key="'BU' + i"
                      ></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>
            </collapsible-section>

            <!-- OVERRIDE RULES SECTION -->
            <collapsible-section title="Override rules" icon="tim-icons icon-lock-circle">

              <div class="row mb-2">
                <div class="col-lg-6">
                  <div class="d-flex align-items-center p-2 border border-primary rounded">
                    <i class="text-white fas fa-info-circle mr-2"></i>
                    <small class="text-white">The following override rules will supersede existing rules when resource time slots overlap.
                      A resource slot must still cover the event for these override rules to take effect.
                      Events without resource slots are not subject to resource management.</small>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <el-switch active-color="#e14eca"
                             v-model="game.block_concurrent_events_when_booked"  />
                  <label> &nbsp; Block <b>all</b> overlapping events when a booking is made
                    <el-popover trigger="hover"
                                placement="right">
                      <div>
                        <div class="popover-body">
                          <badge  type="danger">not recommended</badge><br/>
                          Enabling this setting will ensure that all
                          concurrent slots for <b>any</b> event <br>will be automatically blocked off when one is booked.
                        </div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <el-switch active-color="#e14eca"
                             v-model="game.block_concurrent_same_events_when_booked"  />
                  <label> &nbsp; Block all overlapping events of the <b>same type</b> when a booking is made
                    <el-popover trigger="hover"
                                placement="right">
                      <div>
                        <div class="popover-body">Enabling this setting will ensure that all
                          concurrent slots for <b>the same</b> event <br>will be automatically blocked off when one is booked.
                        </div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <el-switch active-color="#e14eca"
                             v-model="game.block_selected_overlapping_games" />
                  <label> &nbsp; Block <b>select</b> overlapping events when a booking is made
                    <el-popover trigger="hover"
                                placement="right">
                      <div>
                        <div class="popover-body">When this event is booked, only the specifically selected events
                          will have their overlapping time slots blocked automatically.
                        </div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                </div>
              </div>

              <div class="row mt-2" v-if="game.block_selected_overlapping_games">
                <div class="col-lg-6">
                  <label>Select events to block</label>
                  <base-input :error="errs.blockingGames">
                    <el-select
                      v-model="selected_blocking_games"
                      @change="errs.blockingGames = ''"
                      class="select-primary"
                      placeholder="Choose events to block"
                      multiple
                      filterable
                      clearable
                      value-key="id"
                    >
                      <el-option
                        v-for="availableGame in available_games"
                        :key="availableGame.id"
                        :label="availableGame.name"
                        class="select-primary"
                        :value="{id: availableGame.id, name: availableGame.name, duration_minutes: availableGame.duration_minutes}"
                      >
                        <span style="float: left">{{ availableGame.name }}</span>
                        <span style="float: right; color: #8492a6; font-size: 13px">
              {{ availableGame.duration_minutes }}min
            </span>
                      </el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <el-switch active-color="#e14eca"
                             v-model="game.block_selected_overlapping_games_conditional"
                             @change="onConditionalRuleToggle" />
                  <label> &nbsp; Block <b>select</b> overlapping events when <b>fewer than</b> a set number of resources cover the booked time
                    <el-popover trigger="hover"
                                placement="right"
                                width="260">
                      <div>
                        <div class="popover-body" style="word-break: normal; overflow-wrap: break-word; text-align: left;">When this event is booked, the selected events have their overlapping time slots blocked only while fewer than the chosen number of resource time slots cover the booked time, for example when only one host is on shift. Each resource time slot counts once, however many events it can run. Pick a resource category to count only those resources; with Any applicable resource, a categorized resource counts only when this event uses its category.</div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                </div>
              </div>

              <div class="row mt-2" v-if="game.block_selected_overlapping_games_conditional">
                <div class="col-lg-6">
                  <label>Select events to block when resources are short</label>
                  <base-input :error="errs.blockingConditionalGames">
                    <el-select
                      v-model="selected_conditional_blocking_games"
                      @change="errs.blockingConditionalGames = ''"
                      class="select-primary"
                      placeholder="Choose events to block"
                      multiple
                      filterable
                      clearable
                      value-key="id"
                    >
                      <el-option
                        v-for="availableGame in available_games"
                        :key="'CB' + availableGame.id"
                        :label="availableGame.name"
                        class="select-primary"
                        :value="{id: availableGame.id, name: availableGame.name, duration_minutes: availableGame.duration_minutes}"
                      >
                        <span style="float: left">{{ availableGame.name }}</span>
                        <span style="float: right; color: #8492a6; font-size: 13px">
              {{ availableGame.duration_minutes }}min
            </span>
                      </el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>

              <div class="row" v-if="game.block_selected_overlapping_games_conditional">
                <div class="col-lg-3">
                  <label>Fewer than this many resources</label>
                  <base-input :error="errs.blockingCondition" style="width: 180px">
                    <el-input-number size="small" v-model="game.block_selected_resources_below" :min="1" :precision="0" @change="errs.blockingCondition = ''" />
                  </base-input>
                </div>
                <div class="col-lg-3">
                  <label>Count resources in</label>
                  <base-input>
                    <el-select v-model="game.block_selected_resource_category_id" class="select-primary" placeholder="Any applicable resource">
                      <el-option class="select-primary" :value="null" label="Any applicable resource" />
                      <el-option
                        v-for="category in blockSelectCategoryOptions"
                        :label="category.name"
                        :value="category.id"
                        class="select-primary"
                        :key="'BSC' + category.id"
                      >
                        <span :style="'color: ' + (category.color || '#666')">●</span>
                        {{ category.name }}
                      </el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <el-switch active-color="#e14eca"
                             v-model="game.block_same_start_time_events_when_booked" />
                  <label> &nbsp; Block events that start at the <b>same time</b> when a booking is made
                    <el-popover trigger="hover"
                                placement="right"
                                width="260">
                      <div>
                        <div class="popover-body" style="word-break: normal; overflow-wrap: break-word; text-align: left;">When this event is booked, events starting at the <b>exact same date and time</b> are automatically blocked off, as long as a resource slot covers them. The count sets how many events can be booked at the same start time before the rest are blocked; every booking at that start time counts. Select events below to limit the rule to those events. With no selection, the rule applies to every other event.</div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </label>
                </div>
              </div>

              <div class="row mt-2" v-if="game.block_same_start_time_events_when_booked">
                <div class="col">
                  <label>Block once this many events are booked at the same start time</label>
                  <div class="row">
                    <div class="col-lg-3">
                      <base-input style="width: 180px">
                        <el-input-number size="small" v-model="game.block_same_start_time_events_threshold" :min="1" :precision="0" />
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row mt-2" v-if="game.block_same_start_time_events_when_booked">
                <div class="col-lg-6">
                  <label>Select events to block at the same start time</label>
                  <base-input>
                    <el-select
                      v-model="selected_same_start_blocking_games"
                      class="select-primary"
                      placeholder="All events"
                      multiple
                      filterable
                      clearable
                      value-key="id"
                    >
                      <el-option
                        v-for="availableGame in available_games"
                        :key="'SS' + availableGame.id"
                        :label="availableGame.name"
                        class="select-primary"
                        :value="{id: availableGame.id, name: availableGame.name, duration_minutes: availableGame.duration_minutes}"
                      >
                        <span style="float: left">{{ availableGame.name }}</span>
                        <span style="float: right; color: #8492a6; font-size: 13px">
                          {{ availableGame.duration_minutes }}min
                        </span>
                      </el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>
            </collapsible-section>

          </div>

<!--          Pricing-->
          <div v-if="activeTabIndex === 3">
            <collapsible-section title="Pricing model" icon="tim-icons icon-coins" :collapsible="false">
            <div class="row">
              <div class="col-lg-3">
                <base-input label="Pricing type">
                  <el-select
                    label="Select time"
                    v-model="selectedPricingType"
                    class="select-primary"
                    value-key="id"
                    placeholder="Pricing type">
                    <el-option
                      v-for="(pt, index) in pricing_types"
                      class="select-primary"
                      :label="pt.name"
                      :value="{id: pt.id,
                          name: pt.name}"
                      :key="'PT' + index"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="game.cumulative_category_pricing" />
                <label> &nbsp; Category pricing is cumulative
                  <el-popover trigger="hover" placement="right">
                    <div>
                      <div class="popover-body">Cumulative category pricing adjusts per-person rates<br/> based on the total group size across multiple categories.
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="game.hide_pricing_rate" />
                <label> &nbsp; Hide pricing rate on booking site</label>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="game.limit_purchase_category" />
                <label> &nbsp; Limit to one category per purchase
                  <el-popover trigger="hover" placement="right">
                    <div>
                      <div class="popover-body">When enabled, customers can only purchase one pricing category at a time. <br/>
                        When disabled, customers can combine multiple categories in a single purchase.
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>
            </collapsible-section>
            <collapsible-section title="Pricing categories" icon="tim-icons icon-tag" :collapsible="false">
            <div class="row mb-3">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="game.enable_multiple_durations" @change="onDurationsToggle" />
                <label> &nbsp; Enable multiple durations
                  <el-popover trigger="hover" placement="right">
                    <div>
                      <div class="popover-body">Offer this event at several lengths. Each duration has its own pricing categories and tiers. <br/>
                        Customers pick the duration after choosing a time.
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <pricing-categories-editor layout="rows" :categories="activeCategories" :errors="activeErrs" :set-key="String(activeDurationMinutes)" title="" add-label="Add category">
                  <duration-pills v-if="game.enable_multiple_durations" class="mb-3"
                                  :durations="durations" v-model="activeDurationMinutes"
                                  @add="addDuration" @remove="removeDuration" @set-default="setDefaultDuration" />
                </pricing-categories-editor>
              </div>
            </div>
            </collapsible-section>
            <collapsible-section title="Deposits" icon="tim-icons icon-money-coins" :collapsible="false">
            <div class="row">
              <div class="col">
              </div>
            </div>
            <div class="row">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="game.allow_deposit" />
                <label> &nbsp; Allow deposit</label>
              </div>
            </div>
            <div class="row">
              <div class="col" v-if="game.allow_deposit">
                <el-switch active-color="#e14eca"
                           v-model="game.require_deposit" />
                <label> &nbsp; Require only deposit amount during checkout</label>
              </div>
            </div>
            <div class="row">
              <div class="col d-flex mt-2" v-if="game.allow_deposit">
                <base-input label="Deposit type">
                  <el-select
                    v-model="game.deposit_type"
                    class="select-primary"
                    name="deposit_type"
                    placeholder="Value type"
                    value-key="id"
                  >
                    <el-option
                      v-for="(type, index) in valueTypes"
                      class="select-primary"
                      :label="type.name"
                      :value="{name: type.name, id: type.id}"
                      :key="index + 'VT'"
                    ></el-option>
                  </el-select>
                </base-input>
                <base-input class="ml-2" :label="'Deposit amount (' + currency_symbol +')'">
                  <el-input-number v-on:change="validateAmount(game.deposit_amount)"
                                   placeholder="0.00" v-model="game.deposit_amount" :controls="false" :precision="2" :step="0.1" />
                </base-input>
              </div>
            </div>
            </collapsible-section>
          </div>



<!--          Save-->
          <div class="row mt-2" v-if="activeTabIndex !== 4">
            <div class="col">
              <base-button type="default" class="mr-2" v-on:click="cancel()">
                Cancel</base-button>
              <base-button type="primary" v-if="$route.query.id === ''" v-on:click="createGame()">
                Create</base-button>
              <base-button type="primary" v-else v-on:click="saveGame()">
                Save</base-button>

            </div>
          </div>
        </card>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Select,
  Option,
  Tooltip,
  Upload,
  Slider,
  ColorPicker,
  CarouselItem,
  Carousel,
  Switch,
  Dropdown, DropdownMenu, DropdownItem
} from 'element-ui';
  import { Auth } from 'aws-amplify'
  import { ImageUpload } from 'src/components';
  import { API_LOCATION } from '../../../api-config'
  import currencies from "@/assets/currencies.json";
const axios = require('axios')
import _ from "lodash";
import { splitCategoriesByDuration, flattenCategoriesByDuration } from '@/util/pricing'
import { validateSlotPricing } from '@/util/slotPricingValidation'
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import RichTextEditor from '@/components/RichTextEditor'
import draggable from 'vuedraggable'
import ImageEditor from "@/components/ImageEditor";
import {dayOfWeek, formattedDate, localTime} from "@/plugins/dateFormatter";
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";
import { BTable } from 'bootstrap-vue'
import EventRatingsEditor from "./components/EventRatingsEditor.vue";
import TabRail from '@/components/TabRail.vue'
import Badge from "@/components/Badge";
import ObjectiveManager from "./components/ObjectiveManager.vue";

  export default {
    name: "EditGame",
    computed: {
      blockSelectCategoryOptions() {
        const options = [...(this.availableResourceCategories || [])]
        const current = this.game.block_selected_resource_category_id
        if (current && !options.some(c => c.id === current)) {
          options.push({ id: current, name: 'Archived category', color: '#666' })
        }
        return options
      },
      activeDurationSet(){
        if(this.activeDurationMinutes === null || this.activeDurationMinutes === undefined){
          return null
        }
        return this.durations.find(d => Number(d.duration_minutes) === Number(this.activeDurationMinutes) && !d.is_default) || null
      },
      activeCategories(){
        return this.activeDurationSet ? this.activeDurationSet.pricing_categories : this.pricing_categories
      },
      activeErrs(){
        return this.activeDurationSet ? this.activeDurationSet.errs : this.errs.pricing_categories
      },
      upgradeFeeLabel() {
        return this.game.reservation_upgrade_pricing_mode === 'tickets_plus_fee' ? 'Private fee' : 'Buyout price'
      },
      categories() {
        return [
          { name: 'Event details', shortName: 'Details' },
          { name: 'Images', shortName: 'Images' },
          { name: 'Bookings and resources', shortName: 'Bookings' },
          { name: 'Pricing', shortName: 'Pricing' },
          { name: 'Objectives', shortName: 'Objectives' },
        ];
      },
      ratingsBadge() {
        const active = new Set(this.ratingScales.map(scale => scale.id))
        const count = this.ratings.filter(r => active.has(r.rating_scale_id)).length
        return count || ''
      },
      totalResourceTime() {
        const duration = parseInt(this.game.duration_minutes) || 0;
        const preBuffer = parseInt(this.game.pre_buffer_minutes) || 0;
        const postBuffer = parseInt(this.game.buffer_minutes) || 0;
        return preBuffer + duration + postBuffer;
      },
      verticalOptions() {
        // Retired verticals stay listed only while the game still points at one, so its label resolves.
        return this.event_taxonomy.filter(v => v.active || v.id === this.game.vertical_id)
      },
      genreOptions() {
        const vertical = this.event_taxonomy.find(v => v.id === this.game.vertical_id)
        if (!vertical) return []
        return vertical.genres.filter(g => g.active || g.id === this.game.genre_id)
      }
    },
    components: {
      BreadcrumbItem,
      Breadcrumb,
      [Select.name]: Select,
      [Option.name]: Option,
      [Upload.name]: Upload,
      [Slider.name]: Slider,
      [ColorPicker.name]: ColorPicker,
      [Carousel.name]: Carousel,
      [CarouselItem.name]: CarouselItem,
      [Switch.name]: Switch,
      [Dropdown.name]: Dropdown,
      [DropdownMenu.name]: DropdownMenu,
      [DropdownItem.name]: DropdownItem,
      Tooltip,
      ImageUpload,
      RichTextEditor,
      draggable,
      ImageEditor,
      BTable,
      EventRatingsEditor,
      TabRail,
      Badge,
      ObjectiveManager
    },
    data() {
      return {
        archiveDialog: {
          show: false,
          title: '',
          message: '',
          variant: 'warning',
          showCancel: false,
          confirmText: 'OK',
          confirmVariant: 'primary',
          onConfirm: null,
          redirectHome: false
        },
        availableResourceCategories: [],
        resourceCategoryRequirements: [],
        block_units: [
          {
            id: 0,
            name: 'Event slot',
            label: 'event'
          },
          {
            id: 1,
            name: 'Ticket',
            label: 'ticket',
          },
          {
            id: 2,
            name: 'Purchase',
            label: 'purchase',
          },
        ],
        companyId: localStorage.getItem('companyId'),
        companyGroupId: JSON.parse(localStorage.getItem('group')).id,
        payment_gateway_id: JSON.parse(localStorage.getItem('group')).payment_gateway_id,
        time_format: JSON.parse(localStorage.getItem('group')).time_format,
        date_format: JSON.parse(localStorage.getItem('group')).date_format,
        game_name: '',
        game_images: [],
        deviceTargetOptions: [
          { value: 'all', label: 'All devices' },
          { value: 'desktop', label: 'Desktop only' },
          { value: 'mobile', label: 'Mobile only' }
        ],
        valueTypes: [
          {
            name: 'Per booking',
            id: 0
          },
          {
            name: 'Per participant',
            id: 1
          }
        ],
        modals: {
          image: false
        },
        game: {
          name: '',
          min_players_count: 1,
          max_players_count: 1,
          duration_minutes: 0,
          buffer_minutes: 0,
          pre_buffer_minutes: 0,
          required_resource_count: 1,
          deposit_type: {id: 0},
          description: '',
          short_description: '',
          deposit_amount: 0,
          is_public_booking: {id: 0},
          allow_deposit: false,
          require_deposit: false,
          cumulative_category_pricing: false,
          hide_pricing_rate: false,
          limit_purchase_category: false,
          enable_multiple_durations: false,
          block_concurrent_events_when_booked: false,
          block_concurrent_same_events_when_booked: false,
          blocking_unit: {id: 0, label: 'event'},
          block_selected_overlapping_games: false,
          block_selected_overlapping_games_conditional: false,
          block_selected_resources_below: undefined,
          block_selected_resource_category_id: null,
          block_same_start_time_events_when_booked: false,
          block_same_start_time_events_threshold: 1,
          blocked_game_ids: [],
          allow_reservation_type_upgrade: false,
          reservation_upgrade_fee: 0,
          reservation_upgrade_pricing_mode: 'buyout',
          vertical_id: null,
          genre_id: null,
          build_cost: undefined,
          square_footage: undefined
        },
        event_taxonomy: [],
        reservation_upgrade_pricing_modes: [
          { value: 'buyout', label: 'Buyout' },
          { value: 'tickets_plus_fee', label: 'Tickets + fee' }
        ],
        available_games: [], // Games that can be selected for blocking
        selected_blocking_games: [], // For the multi-select component
        selected_conditional_blocking_games: [], // events the conditional select rule blocks (fewer than N resources)
        selected_same_start_blocking_games: [], // OTC-2960: events the same start time rule is limited to
        currencies: currencies.currencies,
        currency_symbol: '$',
        ratings: [],
        ratingScales: [],
        editName: false,
        groups: '',
        file: '',
        selectedGroup: '',
        loading: false,
        errs: {
          name: '',
          blockingCondition: '',
          blockingConditionalGames: '',
          duration: '',
          pricing_categories: {},
          per_person_pricing: [],
          per_game_pricing: '',
          per_category_pricing: [],
          companyGroupId: false,
          resourceCategoryRequirements: '',
          blockingGames: '',
          ratings: {}
        },
        j: 0,
        activeTabIndex: 0,
        pricing_types: [
          {
            id: 1,
            name: 'Per person'
          },
          {
            id: 2,
            name: 'Flat'
          },
        ],
        selectedPricingType: {id: 1},
        per_person_pricing: [],
        per_category_pricing: [],
        per_game_pricing: '',
        pricing_categories: [{
          category: 'Person',
          minimum_participants: 0,
          maximum_participants: undefined,
          default_participants: 0,
          pricing: [{min_players: 1, max_players: 2, price: 0}]
        }],
        //Multiple durations: non-default sets live here; the default set is pricing_categories
        durations: [],
        activeDurationMinutes: null,
        reservation_styles: [
          {
            id: 0,
            name: 'Private booking'
          },
          {
            id: 1,
            name: 'Public booking'
          },
        ],
        dragOptions: {
          animation: 200,
          axis: 'x', // Restrict dragging to the horizontal axis
        },
      }
    },
    methods: {
      onUpgradePricingModeChange(mode) {
        // Reset the amount on mode switch so a buyout-sized number is never reinterpreted as a nominal fee.
        this.game.reservation_upgrade_pricing_mode = mode
        this.game.reservation_upgrade_fee = 0
      },
      onVerticalChange() {
        // A cleared or switched vertical invalidates a genre it doesn't contain.
        const vertical = this.event_taxonomy.find(v => v.id === this.game.vertical_id)
        if (!vertical || !vertical.genres.some(g => g.id === this.game.genre_id)) {
          this.game.genre_id = null
        }
      },
      async loadResourceCategories() {
        if (!this.companyGroupId) return;

        try {
          const token = await this.getAuthToken();
          const config = { headers: { Authorization: token } };
          const companyId = localStorage.getItem('companyId');
          const url = `${API_LOCATION}companies/${companyId}/groups/${this.companyGroupId}/resource-categories`;

          const response = await axios.get(url, config);
          this.availableResourceCategories = response.data.resource_categories || [];
        } catch (error) {
          console.error('Failed to load resource categories:', error);
        }
      },
      addResourceCategoryRequirement() {
        this.resourceCategoryRequirements.push({
          category_id: null,
          required_count: 1,
          allocation_type: 'fixed',
          tiers: [],
          categoryError: '',
          countError: '',
          tiersError: '',
          tiersWarning: '',
          _showDetails: false
        });
      },
      addTier(requirementIndex) {
        this.resourceCategoryRequirements[requirementIndex].tiers.push({
          min_group_size: 1,
          max_group_size: 10,
          required_count: 1,
          blocking_duration_minutes: 60
        });
        this.$set(this.resourceCategoryRequirements[requirementIndex], '_showDetails', true);
        this.checkTierWarnings(requirementIndex);
      },
      removeTier(requirementIndex, tierIndex) {
        this.resourceCategoryRequirements[requirementIndex].tiers.splice(tierIndex, 1);
        this.checkTierWarnings(requirementIndex);
      },
      onAllocationTypeChange(row) {
        this.$set(this.resourceCategoryRequirements[row.index], '_showDetails', row.item.allocation_type === 'tiered');
        this.checkTierWarnings(row.index);
      },
      checkTierWarnings(requirementIndex) {
        let requirement = this.resourceCategoryRequirements[requirementIndex];
        requirement.tiersWarning = '';
        if (requirement.allocation_type !== 'tiered' || !requirement.tiers || requirement.tiers.length === 0) return;

        let sorted = [...requirement.tiers].sort((a, b) => (a.min_group_size || 0) - (b.min_group_size || 0));

        // Check for gaps between tiers
        if (sorted.length > 1) {
          let gaps = [];
          for (let t = 1; t < sorted.length; t++) {
            if (sorted[t].min_group_size > sorted[t - 1].max_group_size + 1) {
              let gapStart = sorted[t - 1].max_group_size + 1;
              let gapEnd = sorted[t].min_group_size - 1;
              gaps.push(gapStart === gapEnd ? `${gapStart}` : `${gapStart}-${gapEnd}`);
            }
          }
          if (gaps.length > 0) {
            requirement.tiersWarning = `Group size ${gaps.join(', ')} not covered by any tier. Bookings with these group sizes will skip the resource check for this category.`;
            return;
          }
        }

        // Check coverage against game player range
        let minPlayers = this.game.min_players_count || 1;
        let maxPlayers = this.game.max_players_count;
        let warnings = [];
        if (sorted[0].min_group_size > minPlayers) {
          let rangeEnd = sorted[0].min_group_size - 1;
          warnings.push(`Group size ${minPlayers}${rangeEnd > minPlayers ? '-' + rangeEnd : ''} below first tier`);
        }
        if (maxPlayers && sorted[sorted.length - 1].max_group_size < maxPlayers) {
          let rangeStart = sorted[sorted.length - 1].max_group_size + 1;
          warnings.push(`Group size ${rangeStart}${maxPlayers > rangeStart ? '-' + maxPlayers : ''} above last tier`);
        }
        if (warnings.length > 0) {
          requirement.tiersWarning = warnings.join('. ') + '. Bookings with these group sizes will skip the resource check for this category.';
        }
      },

      // Remove resource category requirement
      removeResourceCategoryRequirement(index) {
        this.resourceCategoryRequirements.splice(index, 1);
      },

      // Handle category selection change
      onCategoryChange(index, categoryId) {
        // Clear any previous error
        this.resourceCategoryRequirements[index].categoryError = '';

        // Check if this category is already selected in another requirement
        const existingIndex = this.resourceCategoryRequirements.findIndex((req, i) =>
          i !== index && req.category_id === categoryId
        );

        if (existingIndex !== -1) {
          this.resourceCategoryRequirements[index].categoryError = 'This category is already selected';
          this.resourceCategoryRequirements[index].category_id = null;
        }
      },

      // Validate resource category requirements
      validateResourceCategoryRequirements() {
        if (!this.game.allocate_by_resource_category) {
          return true;
        }

        let hasErrors = false;

        for (let i = 0; i < this.resourceCategoryRequirements.length; i++) {
          const requirement = this.resourceCategoryRequirements[i];

          // Reset errors and warnings
          requirement.categoryError = '';
          requirement.countError = '';
          requirement.tiersError = '';
          requirement.tiersWarning = '';

          // Validate category selection
          if (!requirement.category_id) {
            requirement.categoryError = 'Please select a category';
            hasErrors = true;
          }

          if (requirement.allocation_type === 'tiered') {
            // Validate tiers
            if (!requirement.tiers || requirement.tiers.length === 0) {
              requirement.tiersError = 'At least one tier is required';
              this.$set(requirement, '_showDetails', true);
              hasErrors = true;
            } else {
              for (const tier of requirement.tiers) {
                if (!tier.min_group_size || tier.min_group_size < 1) {
                  requirement.tiersError = 'Min group size must be at least 1';
                  hasErrors = true;
                  break;
                }
                if (!tier.max_group_size || tier.max_group_size < tier.min_group_size) {
                  requirement.tiersError = 'Max group size must be greater than or equal to min group size';
                  hasErrors = true;
                  break;
                }
                if (!tier.required_count || tier.required_count < 1) {
                  requirement.tiersError = 'Required count must be at least 1 for each tier';
                  hasErrors = true;
                  break;
                }
                if (!tier.blocking_duration_minutes || tier.blocking_duration_minutes < 1) {
                  requirement.tiersError = 'Blocking duration must be at least 1 minute for each tier';
                  hasErrors = true;
                  break;
                }
              }
              // Check for overlapping ranges and gaps
              if (!hasErrors && requirement.tiers.length > 1) {
                const sorted = [...requirement.tiers].sort((a, b) => a.min_group_size - b.min_group_size);
                for (let t = 1; t < sorted.length; t++) {
                  if (sorted[t].min_group_size <= sorted[t - 1].max_group_size) {
                    requirement.tiersError = 'Tier ranges must not overlap';
                    hasErrors = true;
                    break;
                  }
                }
                // Check for gaps between tiers
                if (!hasErrors) {
                  let gaps = [];
                  for (let t = 1; t < sorted.length; t++) {
                    if (sorted[t].min_group_size > sorted[t - 1].max_group_size + 1) {
                      let gapStart = sorted[t - 1].max_group_size + 1;
                      let gapEnd = sorted[t].min_group_size - 1;
                      gaps.push(gapStart === gapEnd ? `${gapStart}` : `${gapStart}-${gapEnd}`);
                    }
                  }
                  if (gaps.length > 0) {
                    requirement.tiersWarning = `Group size ${gaps.join(', ')} not covered by any tier. Bookings with these group sizes will skip the resource check for this category.`;
                    this.$set(requirement, '_showDetails', true);
                  }
                }
              }
              // Check coverage against game player range
              if (!hasErrors && requirement.tiers.length > 0) {
                const sorted = [...requirement.tiers].sort((a, b) => a.min_group_size - b.min_group_size);
                let warnings = [];
                let minPlayers = this.game.min_players_count || 1;
                let maxPlayers = this.game.max_players_count;
                if (sorted[0].min_group_size > minPlayers) {
                  warnings.push(`Group size ${minPlayers}-${sorted[0].min_group_size - 1} below first tier`);
                }
                if (maxPlayers && sorted[sorted.length - 1].max_group_size < maxPlayers) {
                  warnings.push(`Group size ${sorted[sorted.length - 1].max_group_size + 1}-${maxPlayers} above last tier`);
                }
                if (warnings.length > 0 && !requirement.tiersWarning) {
                  requirement.tiersWarning = warnings.join('. ') + '. Bookings with these group sizes will skip the resource check for this category.';
                  this.$set(requirement, '_showDetails', true);
                }
              }
            }
          } else {
            // Fixed: validate required count
            if (!requirement.required_count || requirement.required_count < 1) {
              requirement.countError = 'Required count must be at least 1';
              hasErrors = true;
            }
          }

          // Check for duplicate categories
          const duplicateIndex = this.resourceCategoryRequirements.findIndex((req, index) =>
            index !== i && req.category_id === requirement.category_id && req.category_id !== null
          );

          if (duplicateIndex !== -1) {
            requirement.categoryError = 'This category is already selected';
            hasErrors = true;
          }
        }

        return !hasErrors;
      },
      async getAvailableGames() {
        try {
          const token = await this.getAuthToken()
          const config = {headers: {Authorization: token}}
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/games?show_taxonomy=true`

          const response = await axios.get(url, config)
          this.available_games = response.data.games.filter(g => !g.archived && g.id !== this.game.id)
          this.event_taxonomy = response.data.event_taxonomy || []
        } catch (error) {
          console.error('Error fetching available games:', error)
        }
      },
      // Active rating scales for the Ratings section. Creates the default Difficulty scale on a venue's first read.
      async getRatingScales() {
        try {
          const token = await this.getAuthToken()
          const config = {headers: {Authorization: token}}
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/rating-scales`
          const response = await axios.get(url, config)
          this.ratingScales = (response.data.scales || []).filter(scale => !scale.archived)
        } catch (error) {
          console.error('Error fetching rating scales:', error)
        }
      },
      // One entry per active scale; ratings on archived scales are left out so the save never touches them.
      ratingsPayload() {
        return this.ratingScales.map(scale => {
          const rating = this.ratings.find(r => r.rating_scale_id === scale.id)
          return rating
            ? { rating_scale_id: scale.id, value: rating.value == null && scale.display_type !== 'labels' ? 0 : rating.value, label: null, icon: rating.icon, color: rating.color, show_to_customers: rating.show_to_customers }
            : { rating_scale_id: scale.id, value: null, label: null, icon: null, color: null, show_to_customers: true }
        })
      },
      cancel(){
        this.$router.push('/games/settings')
      },
      showArchiveAlert(title, message, variant, redirectHome = false){
        this.archiveDialog = { show: true, title, message, variant, showCancel: false, confirmText: 'OK', confirmVariant: 'primary', onConfirm: null, redirectHome }
      },
      // General dialog (alerts and confirms) on the page's one base-alert-dialog.
      showDialog({ title, message, variant = 'warning', showCancel = false, confirmText = 'OK', confirmVariant = 'primary', onConfirm = null, redirectHome = false }){
        this.archiveDialog = { show: true, title, message, variant, showCancel, confirmText, confirmVariant, onConfirm, redirectHome }
      },
      notifySuccess(message){
        this.$notify({ type: 'success', message, icon: 'tim-icons icon-check-2' })
      },
      // 401 and 500 are permission and plan gates on the games endpoints; both send the user home
      // after they acknowledge. A 400 carries the lambda's short message when there is one.
      handleRequestError(err){
        const status = err && err.response ? err.response.status : 0
        if (status === 401) {
          this.showDialog({ title: 'No access', message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', variant: 'danger', redirectHome: true })
          return
        }
        if (status === 500) {
          this.showDialog({ title: 'Not included in your plan', message: 'The feature has not been enabled as part of your subscription plan.', variant: 'danger', redirectHome: true })
          return
        }
        this.showDialog({ title: 'Something went wrong', message: 'The change could not be saved. Please try again.', variant: 'danger' })
      },
      onArchiveDialogConfirm(){
        const { onConfirm, redirectHome } = this.archiveDialog
        this.archiveDialog.show = false
        if(onConfirm){
          onConfirm()
        } else if(redirectHome){
          this.$router.push('/')
        }
      },
      async toggleArchive(item, archived){
        this.loading = true
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/games/' + item.id + '/archive'
        let data = {
          archive:  archived
        }
        try {
          await axios.put(url, data, config)
          this.loading = false
          this.showArchiveAlert('Success', 'The event has been updated.', 'success')
          this.getGame()
        } catch(err) {
          this.loading = false
          console.log(err)
          const status = err.response ? err.response.status : 0
          if(status == 401){
            this.showArchiveAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', true)
          }
          else if(status == 409){
            this.showArchiveAlert('Slot creation in progress', typeof err.response.data === 'string' ? err.response.data : 'Slot creation is still in progress for this event. Wait for it to finish before archiving.', 'warning')
          }
          else if(status == 500){
            this.showArchiveAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', true)
          }
          else {
            this.showArchiveAlert('Error', 'Something went wrong', 'danger')
          }
        }
      },
      toggleActivationPrompt(item){
        const archiving = !item.archived
        this.archiveDialog = {
          show: true,
          title: 'Are you sure?',
          message: archiving
            ? 'This action will archive the event. It will no longer be available for bookings or waivers.'
            : 'This action will unarchive the event. It will become immediately available for bookings and waivers.',
          variant: 'warning',
          showCancel: true,
          confirmText: 'Confirm',
          confirmVariant: archiving ? 'danger' : 'primary',
          onConfirm: () => this.toggleArchive(item, archiving ? 1 : 0),
          redirectHome: false
        }
      },
      getDayOfWeek(d){
        if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
          return dayOfWeek(d) + ', '
        }
        return ''
      },
      getLocalTime(d){
        return formatGroupTime(d)
      },
      getFormattedDate(d){
        return this.getDayOfWeek(d) + formattedDate(this.date_format, d)
      },
      getLocalDate(d){
        return formatGroupDate(d)
      },
      detectCardOrientation(card){
        // Only baked images (neutral metadata) may reshape the card. Legacy
        // compositions were authored against the 300x200 box and must keep it.
        // A probe is used instead of the rendered img's load event because Vue
        // reuses the img element on data refresh and load would not refire.
        const neutral = Number(card.scale) === 100 && !Number(card.x) && !Number(card.y)
        if (!neutral) {
          this.$set(card, 'is_portrait', false)
          return
        }
        const probe = new Image()
        probe.onload = () => {
          this.$set(card, 'is_portrait', probe.naturalHeight > probe.naturalWidth)
        }
        probe.src = card.url
      },
      deleteImagePrompt(image){
        this.showDialog({
          title: 'Delete this image?',
          message: 'The image will be permanently removed from this event.',
          variant: 'warning',
          showCancel: true,
          confirmText: 'Delete',
          confirmVariant: 'danger',
          onConfirm: () => this.deleteImage(image),
        })
      },
      async deleteImage(image){
        this.loading = true
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/images/' + image.id + `?link_id=` + image.link_id

        axios.delete(url, config)
          .then(response => {
            this.loading = false
            this.notifySuccess('Image deleted')
            const imageStateRestore = {}
            this.game_images.forEach(img => {
              if (img.link_id) imageStateRestore[img.link_id] = {
                visible_to_customers: img.visible_to_customers,
                device_target: img.device_target
              }
            })
            this.getGame(null, imageStateRestore)
          })
          .catch(err => {
            this.loading = false
            console.log(err)
            this.handleRequestError(err)
          })
      },
      async updateImage(value){
        this.loading = true
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/images/' + value.image_id

        let data = {
          url: value.url,
          original_url: value.original_url ?? null,
          compose_mode: value.compose_mode ?? null,
          scale: value.scale,
          description: value.description,
          background_color: value.background_color,
          x: value.x,
          y: value.y,
          is_primary: 0,
          object_fit: value.object_fit
        }

        axios.put(url, data, config)
          .then(response => {
            this.loading = false
            if(this.$route.query.id === ''){
              let item = this.game_images.find(el => el.id === value.image_id);
              if (item) {
                item.url = value.url
                item.original_url = value.original_url ?? null
                item.compose_mode = value.compose_mode ?? null
                item.scale = value.scale
                item.description = value.description
                item.background_color = value.background_color
                item.x = value.x
                item.y = value.y
                item.is_primary = 0
                item.object_fit = value.object_fit
                this.detectCardOrientation(item)
              }
            } else {
              const imageStateRestore = {}
              this.game_images.forEach(img => {
                if (img.link_id) imageStateRestore[img.link_id] = {
                  visible_to_customers: img.visible_to_customers,
                  device_target: img.device_target
                }
              })
              this.getGame('update', imageStateRestore)
            }
          })
          .catch(err => {
            this.loading = false
            console.log(err)
            this.handleRequestError(err)
          })
      },
      async saveImage(value){
        this.loading = true
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/images'

        let data = {
          game_id: this.game.id,
          url: value.url,
          original_url: value.original_url ?? null,
          compose_mode: value.compose_mode ?? null,
          scale: value.scale,
          description: value.description,
          background_color: value.background_color,
          x: value.x,
          y: value.y,
          is_primary: 0,
          object_fit: value.object_fit
        }

        axios.post(url, data, config)
          .then(response => {
            this.loading = false
            if(this.$route.query.id === ''){
              data.id = response.data
              data.visible_to_customers = true
              data.device_target = 'all'
              this.game_images.push(data)
              this.detectCardOrientation(data)
            } else {
              const imageStateRestore = {}
              this.game_images.forEach(img => {
                if (img.link_id) imageStateRestore[img.link_id] = {
                  visible_to_customers: img.visible_to_customers,
                  device_target: img.device_target
                }
              })
              this.getGame(null, imageStateRestore)
            }
          })
          .catch(err => {
            this.loading = false
            console.log(err)
            this.handleRequestError(err)
          })
      },
      getAmount(integer) {
        if (integer == null) {
          return;
        }

        // Get currency info
        const currencyInfo = _.find(this.currencies, { code: this.currency });
        const symbol = currencyInfo ? currencyInfo.symbol : this.currency_symbol;
        const isPostfix = currencyInfo ? currencyInfo.postfix : false;

        let formattedAmount;

        if (this.number_format === 1) {
          // European formatting (comma as decimal separator)
          formattedAmount = integer.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
        } else {
          // Standard formatting (dot as decimal separator)
          formattedAmount = parseFloat(integer).toFixed(2);
        }

        if (this.currency_format === 0) {
          // Symbol format
          if (isPostfix) {
            return formattedAmount + symbol;  // 12.34kr
          } else {
            return symbol + formattedAmount;  // $12.34
          }
        } else {
          // Currency code format
          return formattedAmount + ' ' + this.currency;  // 12.34 SEK
        }
      },
      validateAmount(){
        if(isNaN(this.game.deposit_amount)){
          this.game.deposit_amount = 0
          return
        }
      },
      ensureDefaultDuration(){
        if(!this.durations.some(d => d.is_default)){
          const minutes = Number(this.game.duration_minutes) > 0 ? Number(this.game.duration_minutes) : 60
          this.durations.unshift({ duration_minutes: minutes, is_default: true, position: 0, pricing_categories: [], errs: {} })
        }
      },
      onDurationsToggle(on){
        if(on){
          this.ensureDefaultDuration()
        }
        this.activeDurationMinutes = null
      },
      addDuration(minutes){
        this.ensureDefaultDuration()
        const copied = JSON.parse(JSON.stringify(this.pricing_categories))
        this.durations.push({
          duration_minutes: minutes,
          is_default: false,
          position: this.durations.length,
          pricing_categories: copied,
          errs: {}
        })
        this.activeDurationMinutes = minutes
      },
      removeDuration(minutes){
        const idx = this.durations.findIndex(d => Number(d.duration_minutes) === Number(minutes) && !d.is_default)
        if(idx < 0) return
        this.durations.splice(idx, 1)
        if(Number(this.activeDurationMinutes) === Number(minutes)){
          this.activeDurationMinutes = null
        }
      },
      setDefaultDuration(minutes){
        const next = this.durations.find(d => Number(d.duration_minutes) === Number(minutes) && !d.is_default)
        const current = this.durations.find(d => d.is_default)
        if(!next) return
        //The legacy array always holds the default duration's categories, so the two sets swap
        const nextCategories = next.pricing_categories
        const nextErrs = next.errs
        next.pricing_categories = []
        next.errs = {}
        next.is_default = true
        if(current){
          current.is_default = false
          current.pricing_categories = this.pricing_categories
          current.errs = this.errs.pricing_categories
        }
        this.pricing_categories = nextCategories
        this.errs.pricing_categories = nextErrs
        this.activeDurationMinutes = null
      },
      validatePerPersonPricing(){
        //Every duration's set is validated with the shared validator; the first failing set becomes the active pill
        let errors = 0
        let firstFailing
        const def = validateSlotPricing({ enabled: true, categories: this.pricing_categories })
        this.errs.pricing_categories = def.errors
        if(!def.valid){ errors++; firstFailing = null }
        if(this.game.enable_multiple_durations){
          for(const d of this.durations){
            if(d.is_default) continue
            const r = validateSlotPricing({ enabled: true, categories: d.pricing_categories || [] })
            this.$set(d, 'errs', r.errors)
            if(!r.valid || (d.pricing_categories || []).length === 0){
              errors++
              if(firstFailing === undefined) firstFailing = d.duration_minutes
            }
          }
          if(errors > 0 && firstFailing !== undefined){
            this.activeDurationMinutes = firstFailing
          }
        }
        if(errors > 0){
          this.$notify({ type: 'danger', message: 'Fix the highlighted pricing errors before saving', icon: 'tim-icons icon-alert-circle-exc', timeout: 4000 })
        }
        return errors === 0
      },
      isGameValid(){
        let errors = 0
        if(this.game.name === ''){
          this.errs.name = 'Event name cannot be empty'
          errors++
        } else {
          this.errs.name = ''
        }

        if(Number(this.game.duration_minutes) < 0 || isNaN(this.game.duration_minutes)){
          this.errs.duration = 'Invalid duration'
          errors++
        } else {
          this.errs.duration = ''
        }

        // A named-levels rating has no zero, so an attached one needs a level picked.
        const ratingErrors = {}
        this.ratingScales.forEach(scale => {
          const rating = this.ratings.find(r => r.rating_scale_id === scale.id)
          if (rating && scale.display_type === 'labels' && rating.value == null) {
            ratingErrors[scale.id] = 'Choose a level or remove this rating'
            errors++
          }
        })
        this.errs.ratings = ratingErrors
        return errors > 0 ? false : true
      },
      validateResourceCategories(){
        if(this.game.allocate_by_resource_category && this.resourceCategoryRequirements.length === 0){
          this.errs.resourceCategoryRequirements = 'At least one resource category is required when this option is enabled'
          return false
        }
        this.errs.resourceCategoryRequirements = ''
        if (!this.validateResourceCategoryRequirements()) {
          return false
        }
        return true
      },
      // The select rule with no events picked blocks nothing and also switches off the
      // capacity-based blocking for this event's bookings, so it is never a valid setup.
      onConditionalRuleToggle(on) {
        this.errs.blockingCondition = ''
        this.errs.blockingConditionalGames = ''
        if (on && !this.game.block_selected_resources_below) {
          this.$set(this.game, 'block_selected_resources_below', 2)
        }
      },
      validateOverrideRules(){
        if(this.game.block_selected_overlapping_games && this.selected_blocking_games.length === 0){
          this.errs.blockingGames = 'Select at least one event to block, or turn this rule off'
          return false
        }
        this.errs.blockingGames = ''
        if (this.game.block_selected_overlapping_games_conditional) {
          if (this.selected_conditional_blocking_games.length === 0) {
            this.errs.blockingConditionalGames = 'Select at least one event to block, or turn this rule off'
            return false
          }
          const n = Number(this.game.block_selected_resources_below)
          if (!Number.isInteger(n) || n < 1) {
            this.errs.blockingCondition = 'Enter how many resources, 1 or more'
            return false
          }
        }
        this.errs.blockingConditionalGames = ''
        this.errs.blockingCondition = ''
        return true
      },
      async createGame(){
        if(!this.isGameValid()){
          this.activeTabIndex = 0
          return
        }
        if(!this.validatePerPersonPricing()) {
          this.activeTabIndex = 3
          return
        }
        if(!this.validateResourceCategories()){
          this.activeTabIndex = 2
          return
        }
        if(!this.validateOverrideRules()){
          this.activeTabIndex = 2
          return
        }

        this.loading = true
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let companyId = localStorage.getItem('companyId')
        let companyGroupId = JSON.parse(localStorage.getItem('group')).id
        let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/games'
        let data = {
          name: this.game.name,
          allow_deposit: this.game.allow_deposit,
          require_deposit: this.game.require_deposit,
          deposit_amount: this.game.deposit_amount,
          deposit_type: this.game.deposit_type.id,
          min_players_count : parseInt(this.game.min_players_count),
          max_players_count: parseInt(this.game.max_players_count),
          duration_minutes: parseInt(this.game.duration_minutes),
          buffer_minutes: parseInt(this.game.buffer_minutes) || 0,
          pre_buffer_minutes: parseInt(this.game.pre_buffer_minutes) || 0,
          ratings: this.ratingsPayload(),
          description: this.game.description,
          short_description: this.game.short_description,
          img: this.game.img,
          pricing_type_id: this.selectedPricingType.id,
          pricing_categories: flattenCategoriesByDuration(this.pricing_categories, this.game.enable_multiple_durations ? this.durations : []),
          enable_multiple_durations: this.game.enable_multiple_durations ? 1 : 0,
          durations: this.game.enable_multiple_durations ? this.durations.map(d => ({ duration_minutes: Number(d.duration_minutes), is_default: d.is_default ? 1 : 0, position: Number(d.position) || 0 })) : [],
          cumulative_category_pricing: this.game.cumulative_category_pricing,
          hide_pricing_rate: this.game.hide_pricing_rate,
          limit_purchase_category: this.game.limit_purchase_category,
          required_resource_count: this.game.required_resource_count,
          block_concurrent_events_when_booked: this.game.block_concurrent_events_when_booked,
          block_concurrent_same_events_when_booked: this.game.block_concurrent_same_events_when_booked,
          internal_name: this.game.internal_name,
          is_public_booking: this.game.is_public_booking.id,
          game_images: this.game_images,
          blocking_unit: this.game.blocking_unit.label,
          block_selected_overlapping_games: this.game.block_selected_overlapping_games,
          block_same_start_time_events_when_booked: this.game.block_same_start_time_events_when_booked,
          block_same_start_time_events_threshold: parseInt(this.game.block_same_start_time_events_threshold) || 1,
          blocked_game_ids: this.selected_blocking_games.map(g => g.id),
          block_selected_overlapping_games_conditional: this.game.block_selected_overlapping_games_conditional,
          conditional_blocked_game_ids: this.selected_conditional_blocking_games.map(g => g.id),
          block_selected_resources_below: this.game.block_selected_overlapping_games_conditional ? parseInt(this.game.block_selected_resources_below) : null,
          block_selected_resource_category_id: this.game.block_selected_overlapping_games_conditional ? (this.game.block_selected_resource_category_id || null) : null,
          same_start_blocked_event_ids: this.selected_same_start_blocking_games.map(g => g.id),
          allocate_by_resource_category: this.game.allocate_by_resource_category,
          resource_allocations: this.resourceCategoryRequirements,
          allow_reservation_type_upgrade: this.game.allow_reservation_type_upgrade,
          reservation_upgrade_fee: this.game.reservation_upgrade_fee,
          reservation_upgrade_pricing_mode: this.game.reservation_upgrade_pricing_mode,
          vertical_id: this.game.vertical_id || null,
          genre_id: this.game.genre_id || null,
          build_cost: this.game.build_cost == null ? null : this.game.build_cost,
          square_footage: this.game.square_footage == null ? null : this.game.square_footage,
        }

        this.axios.post(url, data, config)
          .then(response => {
            this.notifySuccess('Event created')
            this.$router.push({ path: '/games/settings'})
            this.loading = false
          })
          .catch(err => {
            this.loading = false
            console.log(err)
            this.handleRequestError(err)
          })
      },
      async editGame(confirmReservationStyleChange = false){
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/games/' + this.game.id
        let data = {
          confirm_reservation_style_change: confirmReservationStyleChange ? 1 : 0,
          name: this.game.name,
          allow_deposit: this.game.allow_deposit,
          require_deposit: this.game.require_deposit,
          deposit_amount: this.game.deposit_amount,
          deposit_type: this.game.deposit_type.id,
          min_players_count : parseInt(this.game.min_players_count),
          max_players_count: parseInt(this.game.max_players_count),
          duration_minutes: parseInt(this.game.duration_minutes),
          buffer_minutes: parseInt(this.game.buffer_minutes) || 0,
          pre_buffer_minutes: parseInt(this.game.pre_buffer_minutes) || 0,
          ratings: this.ratingsPayload(),
          description: this.game.description,
          short_description: this.game.short_description,
          img: this.game.img,
          pricing_type_id: this.selectedPricingType.id,
          pricing_categories: flattenCategoriesByDuration(this.pricing_categories, this.game.enable_multiple_durations ? this.durations : []),
          enable_multiple_durations: this.game.enable_multiple_durations ? 1 : 0,
          durations: this.game.enable_multiple_durations ? this.durations.map(d => ({ duration_minutes: Number(d.duration_minutes), is_default: d.is_default ? 1 : 0, position: Number(d.position) || 0 })) : [],
          cumulative_category_pricing: this.game.cumulative_category_pricing,
          hide_pricing_rate: this.game.hide_pricing_rate,
          limit_purchase_category: this.game.limit_purchase_category,
          required_resource_count: this.game.required_resource_count,
          block_concurrent_events_when_booked: this.game.block_concurrent_events_when_booked,
          block_concurrent_same_events_when_booked: this.game.block_concurrent_same_events_when_booked,
          internal_name: this.game.internal_name,
          is_public_booking: this.game.is_public_booking.id,
          game_images: this.game_images,
          blocking_unit: this.game.blocking_unit.label,
          block_selected_overlapping_games: this.game.block_selected_overlapping_games,
          block_same_start_time_events_when_booked: this.game.block_same_start_time_events_when_booked,
          block_same_start_time_events_threshold: parseInt(this.game.block_same_start_time_events_threshold) || 1,
          blocked_game_ids: this.selected_blocking_games.map(g => g.id),
          block_selected_overlapping_games_conditional: this.game.block_selected_overlapping_games_conditional,
          conditional_blocked_game_ids: this.selected_conditional_blocking_games.map(g => g.id),
          block_selected_resources_below: this.game.block_selected_overlapping_games_conditional ? parseInt(this.game.block_selected_resources_below) : null,
          block_selected_resource_category_id: this.game.block_selected_overlapping_games_conditional ? (this.game.block_selected_resource_category_id || null) : null,
          same_start_blocked_event_ids: this.selected_same_start_blocking_games.map(g => g.id),
          allocate_by_resource_category: this.game.allocate_by_resource_category,
          resource_allocations: this.resourceCategoryRequirements,
          allow_reservation_type_upgrade: this.game.allow_reservation_type_upgrade,
          reservation_upgrade_fee: this.game.reservation_upgrade_fee,
          reservation_upgrade_pricing_mode: this.game.reservation_upgrade_pricing_mode,
          vertical_id: this.game.vertical_id || null,
          genre_id: this.game.genre_id || null,
          build_cost: this.game.build_cost == null ? null : this.game.build_cost,
          square_footage: this.game.square_footage == null ? null : this.game.square_footage,
        }

        return new Promise( function(resolve) {
          axios.put(url, data, config)
            .then(response => {
              resolve(response)
            })
            .catch(err => {
              resolve(err)
            })
        })
      },
      getAuthToken() {
        return new Promise ( function(resolve) {
          Auth.currentSession()
            .then(data =>{
              resolve(data.idToken.jwtToken)
            })
            .catch(err => console.log(err))
        })
      },
      async getGroups() {
        this.loading = true
        var token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let companyId = localStorage.getItem('companyId')
        let url = API_LOCATION + 'companies/' + companyId + '/groups'

        axios.get(url, config)
          .then(response => {
            this.loading = false
            this.groups = response.data
          })
          .catch(err => {
            this.loading = false
            console.log(err)
            if(err.response && err.response.status == 401){
              this.$router.push('/forbidden')
            } else {
              this.handleRequestError(err)
            }
        })
      },
      toggleEditName() {
        this.editName = !this.editName
      },
      async saveGame () {
        if(!this.isGameValid()){
          this.activeTabIndex = 0
          return
        }
        if(!this.validateResourceCategories()){
          this.activeTabIndex = 2
          return
        }
        if(!this.validateOverrideRules()){
          this.activeTabIndex = 2
          return
        }
        if(!this.validatePerPersonPricing()) {
          this.activeTabIndex = 3
          return
        }

        this.loading = true
        await this.saveFile()

        // editGame resolves with the axios error instead of throwing; only report success on a real 2xx.
        let res = await this.editGame()
        this.loading = false
        if (res && res.response) {
          const gate = this.reservationStyleGate(res.response)
          if (gate) {
            this.askReservationStyleChange(gate)
            return
          }
          this.handleRequestError(res)
          return
        }
        this.notifySuccess('Event saved')
        await this.getGame()
      },
      // The save is refused with 409 when Reservation style changes on an event that has
      // upcoming bookings; the body carries the count. Anything else is not this gate.
      reservationStyleGate(response){
        if (!response || response.status !== 409) return null
        let body = response.data
        if (typeof body === 'string') {
          try { body = JSON.parse(body) } catch (e) { return null }
        }
        return body && body.error === 'reservation_style_change_needs_confirmation' ? body : null
      },
      askReservationStyleChange(gate){
        const n = Number(gate.upcoming_bookings) || 0
        const bookings = n === 1 ? 'booking' : 'bookings'
        const keep = n === 1 ? 'It keeps' : 'They keep'
        const message = `This event has ${n} upcoming ${bookings}. ${keep} the reservation style ${n === 1 ? 'it was' : 'they were'} booked with. The new style applies only to slots with no bookings yet.`
        this.showDialog({
          title: 'Change reservation style?',
          message,
          variant: 'warning',
          showCancel: true,
          confirmText: 'Change style',
          onConfirm: async () => {
            this.loading = true
            const res = await this.editGame(true)
            this.loading = false
            if (res && res.response) {
              this.handleRequestError(res)
              return
            }
            this.notifySuccess('Event saved')
            await this.getGame()
          }
        })
      },
      async getSignedURL (file) {
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let url = API_LOCATION + 'upload'

        let payload = {
          filePath: file.name,
          contentType: file.type
        }

        return new Promise ( function(resolve) {
          axios.post(url, payload, config)
            .then((res) => {
              resolve(res.data)
            })
            .catch((err) => {
              console.error(err)
            })
        })
      },
      async saveFile() {
        if(this.file != ''){
          this.uploadUrl = await this.getSignedURL(this.file)
          let uploaded = await this.uploadFile(this.file)
          //Get the image URL
          let imgUrl = this.uploadUrl.split("?")
          this.game.img = imgUrl[0]
        }
      },
      setSelectedGroup(){
        this.selectedGroup = this.game.company_group_id
      },
      uploadFile (file) {
        let url = this.uploadUrl
        let config = { headers: {
            'Content-Type': file.type
          }}

        let payload = file
        return new Promise ( function(resolve) {
          axios.put(url, payload, config)
            .then((res) => {
              resolve(res)
            })
            .catch((err) => {
              console.error(err)
            })
        })
      },
      async getGame(source, imageStateRestore){
        this.loading = true
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        let gameId = this.$route.query.id
        let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/games/' + gameId
        axios.get(url, config)
          .then(response => {
            this.game = response.data.game
            this.ratings = response.data.ratings || []
            this.game_images = response.data.game_images.map(img => ({
              ...img,
              device_target: img.device_target || 'all'
            }))
            for(let n in this.game_images){
              this.game_images[n].visible_to_customers = Boolean(this.game_images[n].visible_to_customers)
              this.detectCardOrientation(this.game_images[n])
            }
            if (imageStateRestore) {
              for (let n in this.game_images) {
                if (this.game_images[n].link_id in imageStateRestore) {
                  this.game_images[n].visible_to_customers = imageStateRestore[this.game_images[n].link_id].visible_to_customers
                  this.game_images[n].device_target = imageStateRestore[this.game_images[n].link_id].device_target
                }
              }
            }
            this.game_name = this.game.internal_name ? this.game.internal_name : this.game.name
            this.game.is_public_booking = { id: this.game.is_public_booking}
            this.game.block_concurrent_events_when_booked = Boolean(this.game.block_concurrent_events_when_booked)
            this.game.block_concurrent_same_events_when_booked = Boolean(this.game.block_concurrent_same_events_when_booked)
            this.game.block_selected_overlapping_games = Boolean(this.game.block_selected_overlapping_games)
            this.game.block_same_start_time_events_when_booked = Boolean(this.game.block_same_start_time_events_when_booked)
            this.$set(this.game, 'block_same_start_time_events_threshold', Number(this.game.block_same_start_time_events_threshold) || 1)
            this.game.block_selected_overlapping_games_conditional = Boolean(this.game.block_selected_overlapping_games_conditional)
            const resourcesBelow = Number(this.game.block_selected_resources_below)
            this.$set(this.game, 'block_selected_resources_below', Number.isInteger(resourcesBelow) && resourcesBelow >= 1 ? resourcesBelow : undefined)
            this.$set(this.game, 'block_selected_resource_category_id', this.game.block_selected_resource_category_id || null)
            this.game.cumulative_category_pricing = Boolean(this.game.cumulative_category_pricing)
            this.game.allow_deposit = Boolean(this.game.allow_deposit)
            this.game.require_deposit = Boolean(this.game.require_deposit)
            this.game.hide_pricing_rate = Boolean(this.game.hide_pricing_rate)
            this.game.limit_purchase_category = Boolean(this.game.limit_purchase_category)
            this.game.enable_multiple_durations = Boolean(this.game.enable_multiple_durations)
            this.game.allocate_by_resource_category = Boolean(this.game.allocate_by_resource_category)
            this.game.allow_reservation_type_upgrade = Boolean(this.game.allow_reservation_type_upgrade)
            this.$set(this.game, 'reservation_upgrade_pricing_mode', this.game.reservation_upgrade_pricing_mode || 'buyout')
            this.game.blocking_unit = _.find(this.block_units, {label: this.game.blocking_unit})
            this.game.deposit_type = { id: this.game.deposit_type}
            //DECIMAL comes back as a string; el-input-number needs a number, and undefined (not null) to show empty
            this.game.build_cost = this.game.build_cost == null ? undefined : Number(this.game.build_cost)
            this.game.square_footage = this.game.square_footage == null ? undefined : this.game.square_footage
            this.pricing_types = response.data.pricing_types
            this.selectedPricingType = {id: this.game.pricing_type_id }
            this.selected_blocking_games = response.data.blocked_game_ids || []
            this.selected_conditional_blocking_games = response.data.conditional_blocked_game_ids || []
            this.selected_same_start_blocking_games = response.data.same_start_blocked_event_ids || []
            this.resourceCategoryRequirements = (response.data.resource_allocations || []).map(req => ({
              ...req,
              allocation_type: req.allocation_type || 'fixed',
              tiers: req.tiers || [],
              categoryError: '',
              countError: '',
              tiersError: '',
              tiersWarning: '',
              _showDetails: false
            }))

            // Check tier warnings on load
            for (let i = 0; i < this.resourceCategoryRequirements.length; i++) {
              this.checkTierWarnings(i);
            }

            this.pricing_categories = response.data.pricing_categories

            for(let n in this.pricing_categories){
              this.pricing_categories[n].maximum_participants = this.pricing_categories[n].maximum_participants > 0 ? this.pricing_categories[n].maximum_participants : undefined
              if(!this.pricing_categories[n].pricing){
                this.pricing_categories[n].pricing = [{min_players: 1, max_players: 2, price: 0}]
              } else {
                try {
                  const parsed = JSON.parse(this.pricing_categories[n].pricing)
                  this.pricing_categories[n].pricing = Array.isArray(parsed) ? parsed : [{min_players: 1, max_players: 2, price: 0}]
                } catch (e) {
                  console.error('Invalid pricing tiers for category', this.pricing_categories[n].id, e)
                  this.pricing_categories[n].pricing = [{min_players: 1, max_players: 2, price: 0}]
                  this.$notify({type: 'danger', icon: 'tim-icons icon-alert-circle-exc', timeout: 0, message: 'Pricing tiers for "' + this.pricing_categories[n].category + '" could not be loaded. Do not save until they are restored.'})
                }
              }
            }

            //Multiple durations: split the flat list into the default set and one set per duration
            const apiDurations = Array.isArray(response.data.durations) ? response.data.durations : []
            const split = splitCategoriesByDuration(this.pricing_categories, apiDurations)
            this.pricing_categories = split.defaultCategories
            this.errs.pricing_categories = {}
            this.durations = apiDurations.map(d => {
              const isDefault = Number(d.is_default) === 1
              const cats = isDefault ? [] : (split.byMinutes.get(Number(d.duration_minutes)) || [])
              return { duration_minutes: Number(d.duration_minutes), is_default: isDefault, position: Number(d.position) || 0, pricing_categories: cats, errs: {} }
            })
            this.activeDurationMinutes = null
            if(this.game.enable_multiple_durations){
              this.ensureDefaultDuration()
            }

            this.getGroups()
            this.setSelectedGroup()
            this.loading = false
          })
          .catch(err => {
            this.loading = false
            console.log(err)
            this.handleRequestError(err)
          })
      }
    },
    mounted () {
      this.currency_symbol =  _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol
      if(this.$route.query.id !== ''){
        this.getGame().then(() => {
          this.getAvailableGames()
        })
      } else {
        this.getAvailableGames()
      }
      this.loadResourceCategories();
      this.getRatingScales();
      if (this.$route.query.tab !== undefined) {
        this.activeTabIndex = Number(this.$route.query.tab);
      }
    },
    watch: {
      '$route.query.tab'(newTab) {
        if (newTab !== undefined) {
          this.activeTabIndex = Number(newTab);
        }
      }
    }
  }
</script>

<style scoped>
/* Duration / build cost / square footage row: uniform height and full-width
   inputs so the three fields read as one aligned group */
.event-metrics-row ::v-deep .el-input-number {
  width: 100%;
}
.event-metrics-row ::v-deep .el-input-number.is-without-controls .el-input__inner {
  text-align: left;
}

/* Amount input with currency prefix (global amount-input-group pattern) */
.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: transparent;
}
.amount-input-group:focus-within {
  border-color: #e14eca;
}
.amount-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.875rem;
  background: rgba(225, 78, 202, 0.15);
  font-weight: 600;
  font-size: 0.875rem;
  min-width: 40px;
  color: #e14eca;
}
.amount-input-wrapper {
  flex: 1;
}
.amount-input-wrapper ::v-deep .form-control {
  border: none;
  border-radius: 0;
  background: transparent;
}
.amount-input-wrapper ::v-deep .el-input-number {
  width: 100%;
}
.amount-input-wrapper ::v-deep .el-input-number .el-input__inner {
  text-align: left;
  color: white;
  background: transparent;
  border: none;
}

.resource-tiers-panel {
  background: rgba(30, 30, 40, 0.3);
  border-radius: 6px;
}

.gift-cards-wrapper {
  display: flex;
  flex-wrap: wrap; /* Allow items to wrap to the next row */
  gap: 20px; /* Space between cards */
  padding: 10px; /* Padding around the container */
}

.draggable-container {
  display: flex;
  flex-wrap: wrap; /* Ensure draggable items also wrap */
  gap: 20px; /* Spacing between items */
}

.gift-card-item {
  flex: 0 0 auto; /* Keep items fixed size */
  position: relative; /* Ensure the delete button is positioned relative to the container */
}

.gift-card-container {
  width: 300px; /* Card width */
  height: 200px; /* Card height */
  border-radius: 15px; /* Rounded corners */
  overflow: hidden; /* Ensure content fits within rounded corners */
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25); /* Shadow for depth */
  background: #fff;
  position: relative; /* Allows positioning of the delete button */
}

.gift-card-container.is-portrait {
  width: 150px; /* 150x200 keeps row height and yields 3:4 */
}

.gift-card-container img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* Ensure the image scales properly */
}

.hidden-badge {
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
}

.edit-button {
  position: absolute;
  top: 6px;
  right: 42px;
  z-index: 10; /* Ensure it appears above the image */
  display: flex;
  align-items: center;
}

.delete-button {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 10; /* Ensure it appears above the image */
}

/* Card action buttons: neutral chips over the image, subtle darken on hover.
   The edit chip is rendered inside ImageEditor, so it is pierced with ::v-deep. */
.gift-card-container .edit-button ::v-deep .btn,
.gift-card-container .delete-button {
  width: 30px;
  height: 30px;
  min-width: 30px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5) !important;
  background-image: none !important;
  box-shadow: none !important;
  transition: background-color 0.15s ease;
}

.gift-card-container .edit-button ::v-deep .btn i,
.gift-card-container .delete-button i {
  color: #fff !important;
  margin: 0;
}

.gift-card-container .edit-button ::v-deep .btn:hover,
.gift-card-container .delete-button:hover {
  background-color: rgba(0, 0, 0, 0.72) !important;
}

/* Resource tier rows — mirrors EditPricingView price-tier pattern */
.resource-tiers-container {
  margin-left: 6px;
  border-left: 2px solid rgba(225, 78, 202, 0.85);
  padding-left: 10px;
}

.resource-tier-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 6px 8px;
  margin-bottom: 4px;
  border-radius: 4px;
  background: transparent;
  transition: background 0.15s ease;
}

.resource-tier-row.tier-even {
  background: rgba(255, 255, 255, 0.025);
}

.resource-tier-index {
  font-size: 10px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.2);
  padding-bottom: 10px;
  min-width: 12px;
  text-align: center;
  flex-shrink: 0;
}

.resource-tier-field {
  min-width: 0;
}

.resource-tier-field--range {
  flex: 1.4;
}

.resource-tier-field--narrow {
  flex: 0.8;
}

.resource-tier-field-label {
  display: block;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 4px;
}

.resource-range-input-group {
  display: flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: transparent;
  height: 32px;
}

.resource-range-input-group:focus-within {
  border-color: #e14eca;
}

.resource-range-separator {
  color: rgba(255, 255, 255, 0.35);
  font-size: 13px;
  flex-shrink: 0;
  user-select: none;
  padding: 0 2px;
}

.resource-single-input-group {
  display: flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: transparent;
  height: 32px;
}

.resource-single-input-group:focus-within {
  border-color: #e14eca;
}

.resource-tier-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  padding-bottom: 8px;
  flex-shrink: 0;
  width: 44px;
}

.resource-tier-action-slot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
}

.resource-tier-remove-btn,
.resource-tier-add-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 12px;
}

.resource-tier-remove-btn {
  color: #f5365c;
}

.resource-tier-remove-btn:hover {
  color: #ff6b88;
}

.resource-tier-add-btn {
  color: #2dce89;
  font-size: 12px;
}

.resource-tier-add-btn:hover {
  color: #5fe0a8;
}
</style>

<!-- Unscoped styles for el-input-number inside resource tiers -->
<style lang="scss">
/* Event details rich-text editors: dark frame + toolbar chrome wrapping a light WYSIWYG paper
   surface. Keeps the editing surface light (matches the usually-light booking site) while the
   frame blends into the dark form. */
// Description and highlight side by side, both at the same fixed height.
.event-rte-col .event-rte {
  // Fixed, compact editing area: long descriptions scroll inside the editor instead of
  // growing the page, and both editors stay the same height.
  .ql-container {
    height: 240px;
  }
  .ql-editor {
    height: 100%;
    overflow-y: auto;
  }
}

.event-rte {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.5rem;
  background: rgba(30, 30, 47, 0.6);
  overflow: hidden;

  .ql-toolbar.ql-snow {
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.03);
  }
  .ql-toolbar.ql-snow .ql-stroke { stroke: rgba(255, 255, 255, 0.6); }
  .ql-toolbar.ql-snow .ql-fill { fill: rgba(255, 255, 255, 0.6); }
  .ql-toolbar.ql-snow .ql-picker-label { color: rgba(255, 255, 255, 0.7); }
  .ql-toolbar.ql-snow .ql-picker-label .ql-stroke { stroke: rgba(255, 255, 255, 0.7); }
  .ql-toolbar.ql-snow button:hover .ql-stroke,
  .ql-toolbar.ql-snow .ql-picker-label:hover .ql-stroke { stroke: #e14eca; }
  .ql-toolbar.ql-snow button:hover .ql-fill { fill: #e14eca; }
  .ql-toolbar.ql-snow button.ql-active .ql-stroke { stroke: #e14eca; }
  .ql-toolbar.ql-snow button.ql-active .ql-fill { fill: #e14eca; }

  .ql-container.ql-snow {
    border: none;
    background: transparent;
    padding: 8px;
  }
  .ql-editor {
    background: #ffffff;
    color: #000000;
    border-radius: 0.375rem;
  }
}

/* Light mode: swap the dark frame for a plain light card so the editors match
   the surrounding inputs instead of rendering as gray slabs */
.white-content .event-rte {
  border-color: rgba(0, 0, 0, 0.15);
  background: #ffffff;

  .ql-toolbar.ql-snow {
    background: rgba(0, 0, 0, 0.03);
    border-bottom-color: rgba(0, 0, 0, 0.08);
  }
  .ql-toolbar.ql-snow .ql-stroke { stroke: rgba(0, 0, 0, 0.55); }
  .ql-toolbar.ql-snow .ql-fill { fill: rgba(0, 0, 0, 0.55); }
  .ql-toolbar.ql-snow .ql-picker-label { color: rgba(0, 0, 0, 0.6); }
  .ql-toolbar.ql-snow .ql-picker-label .ql-stroke { stroke: rgba(0, 0, 0, 0.6); }
  .ql-toolbar.ql-snow button:hover .ql-stroke,
  .ql-toolbar.ql-snow .ql-picker-label:hover .ql-stroke { stroke: #e14eca; }
  .ql-toolbar.ql-snow button:hover .ql-fill { fill: #e14eca; }
  .ql-toolbar.ql-snow button.ql-active .ql-stroke { stroke: #e14eca; }
  .ql-toolbar.ql-snow button.ql-active .ql-fill { fill: #e14eca; }
}

.resource-range-input-group .el-input-number,
.resource-single-input-group .el-input-number {
  width: 100%;

  .el-input__inner {
    border: none;
    background: transparent;
    color: white;
    text-align: center;
    padding: 0 4px;
    height: 32px;
    line-height: 32px;
    font-size: 13px;
  }
}

.white-content {
  .resource-tier-field-label {
    color: rgba(0, 0, 0, 0.5);
  }

  .resource-tier-index {
    color: rgba(0, 0, 0, 0.2);
  }

  .resource-range-input-group,
  .resource-single-input-group {
    border-color: rgba(0, 0, 0, 0.2);

    &:focus-within {
      border-color: #e14eca;
    }
  }

  .resource-range-separator {
    color: rgba(0, 0, 0, 0.35);
  }

  .resource-range-input-group .el-input-number .el-input__inner,
  .resource-single-input-group .el-input-number .el-input__inner {
    color: #344675 !important;
    background: transparent !important;
  }

  .el-input-number .el-input__inner {
    background: transparent !important;
  }

  .resource-tiers-panel {
    background: rgba(0, 0, 0, 0.04);
  }

  .resource-tiers-panel small.text-white {
    color: #344675 !important;
  }
}
</style>
